// view-as.js — admin "View as" role emulation (an HONEST UI lens, not impersonation).
//
// An admin/owner can view the app through a Doctor / Nurse / Receptionist LENS:
// the nav pills, the role home, the route gates and any role-scoped UI branch
// all resolve to the EMULATED role, so the admin sees exactly the surface that
// role sees. This changes ONLY the UI lens — it is deliberately NOT
// impersonation:
//   • The admin keeps their OWN identity and their OWN Medplum session /
//     AccessPolicy. We never swap tokens or currentUser.
//   • Therefore the DATA shown is still whatever the admin's own hospital-wide
//     RLS (row-level security) returns — an admin sees hospital-wide data, and
//     that is correct. AppShell renders a persistent amber banner stating this
//     plainly while the lens is active.
//
// Design — minimal blast radius + escalation-safe:
//   • The override is a single localStorage key, read through effectiveRole()
//     at the two role-read choke points: the AppShell identity read (nav pills +
//     role home + banner) and the App.jsx route gate. Nothing else changes.
//   • effectiveRole() substitutes the emulated role ONLY when the ACTUAL role is
//     an admin actor (owner/admin). Every emulatable role is a STRICT SUBSET of
//     admin's reach, so the substitution can only make gates MORE restrictive —
//     it can never grant a route the admin couldn't already reach. A non-admin
//     with a stray override in storage is returned their real role unchanged.

export const VIEW_AS_KEY = 'healther.viewAs';

// The roles an admin may emulate, and the home each one lands on. Kept LOCAL
// (rather than importing App.jsx's HOME_BY_ROLE) so the shell never has to
// import the router module — avoids a shell⇄router import cycle. These homes
// match HOME_BY_ROLE for doctor/nurse/receptionist verbatim.
export const EMULATABLE = [
  { role: 'doctor', label: 'Doctor', home: '/queue' },
  { role: 'nurse', label: 'Nurse', home: '/nurse' },
  { role: 'receptionist', label: 'Receptionist', home: '/reception' },
];
const EMULATABLE_ROLES = EMULATABLE.map((e) => e.role);

// The real actor must be one of these to use view-as at all.
const ADMIN_ACTORS = ['owner', 'admin'];

/** True when `actualRole` (the REAL logged-in role) may use the View-as menu. */
export function isViewAsActor(actualRole) {
  return ADMIN_ACTORS.includes(String(actualRole || '').toLowerCase());
}

/** The currently-stored emulated role, or '' if none / invalid. */
export function getViewAs() {
  try {
    const v = String(window.localStorage.getItem(VIEW_AS_KEY) || '').toLowerCase();
    return EMULATABLE_ROLES.includes(v) ? v : '';
  } catch (_) { return ''; }
}

/** Store an emulated role (ignored unless it is a known emulatable role). */
export function setViewAs(role) {
  const r = String(role || '').toLowerCase();
  if (!EMULATABLE_ROLES.includes(r)) return;
  try { window.localStorage.setItem(VIEW_AS_KEY, r); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent('healther:view-as', { detail: r })); } catch (_) {}
}

/** Drop the override (Back to admin / Exit / sign-out). */
export function clearViewAs() {
  try { window.localStorage.removeItem(VIEW_AS_KEY); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent('healther:view-as', { detail: '' })); } catch (_) {}
}

/**
 * The effective role for UI / nav / gating.
 *   • When `actualRole` is an admin actor AND a valid override is stored → the
 *     emulated role.
 *   • Otherwise → `actualRole` unchanged.
 * Pure and escalation-safe (see the file header). Returns a lowercase string.
 */
export function effectiveRole(actualRole) {
  const actual = String(actualRole || '').toLowerCase();
  if (!isViewAsActor(actual)) return actual;
  return getViewAs() || actual;
}

/** The human label for an emulated role (for the banner), or '' if not emulating. */
export function viewAsLabel(role) {
  const hit = EMULATABLE.find((e) => e.role === String(role || '').toLowerCase());
  return hit ? hit.label : '';
}
